import React, { useState, useRef, useCallback, useEffect } from "react";

/**
 * Professional crop adjustment view with 8 draggable handles:
 * - 4 corner handles (TL, TR, BR, BL)
 * - 4 mid-edge handles (Top, Right, Bottom, Left) that move whole edges
 * - Interactive rule-of-thirds perspective grid
 * - Real-time magnifying loupe showing zoomed edge alignment
 * - 1-tap "Full Page" and "Reset / Auto-Detect" buttons
 *
 * Precise Stage Alignment:
 * The stage (<div className="ds-crop-stage">) is mathematically sized and centered to match
 * the exact aspect ratio of the captured canvas. Both the image and the SVG overlay stretch
 * 100% within the stage, guaranteeing 100% pixel-perfect alignment between visual document
 * edges and draggable handles.
 */
const CropAdjustView = ({ canvas, corners, onApply, onCancel }) => {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const loupeCanvasRef = useRef(null);
  const initialCornersRef = useRef(null);
  const dragRef = useRef(null);

  const [adjustedCorners, setAdjustedCorners] = useState(null);
  const [displayUrl, setDisplayUrl] = useState(null);
  const [stageDims, setStageDims] = useState({ w: 0, h: 0, left: 0, top: 0 });
  const [draggingTarget, setDraggingTarget] = useState(null);
  const [loupeState, setLoupeState] = useState({ visible: false, x: 0, y: 0 });

  // ── 1. Validate & Initialize Corners ──────────────────────────────
  const isValidCorner = (c) =>
    c && typeof c.x === "number" && !isNaN(c.x) && typeof c.y === "number" && !isNaN(c.y);

  useEffect(() => {
    let validCorners = null;
    if (Array.isArray(corners) && corners.length === 4 && corners.every(isValidCorner)) {
      validCorners = corners.map((c) => ({ ...c }));
    } else {
      validCorners = [
        { x: canvas.width * 0.08, y: canvas.height * 0.08 },
        { x: canvas.width * 0.92, y: canvas.height * 0.08 },
        { x: canvas.width * 0.92, y: canvas.height * 0.92 },
        { x: canvas.width * 0.08, y: canvas.height * 0.92 },
      ];
    }
    initialCornersRef.current = validCorners;
    setAdjustedCorners(validCorners);
  }, [canvas, corners]);

  // ── 2. Convert Canvas to Display URL ──────────────────────────────
  useEffect(() => {
    if (!canvas) return;
    try {
      const url = canvas.toDataURL("image/jpeg", 0.9);
      setDisplayUrl(url);
    } catch (e) {
      console.error("Canvas toDataURL failed:", e);
    }
  }, [canvas]);

  // ── 3. Exact Mathematical Stage Sizing ────────────────────────────
  const updateStageDims = useCallback(() => {
    const container = containerRef.current;
    if (!container || !canvas || !canvas.width || !canvas.height) return;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    if (!cw || !ch) return;

    const cAspect = cw / ch;
    const imgAspect = canvas.width / canvas.height;

    let w, h, left, top;
    if (imgAspect > cAspect) {
      w = cw;
      h = cw / imgAspect;
      left = 0;
      top = (ch - h) / 2;
    } else {
      h = ch;
      w = ch * imgAspect;
      left = (cw - w) / 2;
      top = 0;
    }

    setStageDims({ w, h, left, top });
  }, [canvas]);

  useEffect(() => {
    updateStageDims();
    window.addEventListener("resize", updateStageDims);

    let ro;
    if (window.ResizeObserver && containerRef.current) {
      ro = new ResizeObserver(() => updateStageDims());
      ro.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener("resize", updateStageDims);
      if (ro) ro.disconnect();
    };
  }, [updateStageDims]);

  // ── 4. Coordinate Mapping: Screen (clientX, clientY) -> Canvas ─────
  const screenToCanvas = useCallback(
    (clientX, clientY) => {
      const stage = stageRef.current;
      if (!stage || !canvas) return null;
      const rect = stage.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;

      const relX = clientX - rect.left;
      const relY = clientY - rect.top;
      return {
        x: Math.max(0, Math.min(canvas.width, (relX / rect.width) * canvas.width)),
        y: Math.max(0, Math.min(canvas.height, (relY / rect.height) * canvas.height)),
      };
    },
    [canvas]
  );

  // ── 5. Loupe Magnifier ─────────────────────────────────────────────
  const updateLoupe = useCallback(
    (focalCanvasPt, screenX, screenY) => {
      const loupeCanvas = loupeCanvasRef.current;
      if (!loupeCanvas || !canvas || !focalCanvasPt) return;

      const ctx = loupeCanvas.getContext("2d");
      if (!ctx) return;

      const size = 110;
      ctx.clearRect(0, 0, size, size);

      // 2.3x zoom
      const zoom = 2.3;
      const srcSize = size / zoom;
      const sx = focalCanvasPt.x - srcSize / 2;
      const sy = focalCanvasPt.y - srcSize / 2;

      ctx.drawImage(canvas, sx, sy, srcSize, srcSize, 0, 0, size, size);

      // Reticle crosshairs
      ctx.strokeStyle = "#2196F3";
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(size / 2, 12);
      ctx.lineTo(size / 2, size - 12);
      ctx.moveTo(12, size / 2);
      ctx.lineTo(size - 12, size / 2);
      ctx.stroke();

      // Center target ring
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, 10, 0, Math.PI * 2);
      ctx.stroke();

      // Position loupe offset above finger
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (containerRect) {
        const localX = screenX - containerRect.left;
        const localY = screenY - containerRect.top;
        const topOffset = localY > 130 ? -85 : 95;
        setLoupeState({
          visible: true,
          x: Math.max(60, Math.min(containerRect.width - 60, localX)),
          y: Math.max(60, Math.min(containerRect.height - 60, localY + topOffset)),
        });
      }
    },
    [canvas]
  );

  // ── 6. Pointer Down Handlers ───────────────────────────────────────
  const handlePointerDown = useCallback(
    (type, index) => (e) => {
      e.preventDefault();
      e.stopPropagation();

      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      const clientY = e.clientY ?? e.touches?.[0]?.clientY;
      const pt = screenToCanvas(clientX, clientY);
      if (!pt) return;

      dragRef.current = {
        type,
        index,
        startPt: pt,
        startCorners: adjustedCorners.map((c) => ({ ...c })),
      };
      setDraggingTarget({ type, index });

      if (type === "corner") {
        updateLoupe(adjustedCorners[index], clientX, clientY);
      } else {
        const c1 = adjustedCorners[index];
        const c2 = adjustedCorners[(index + 1) % 4];
        updateLoupe({ x: (c1.x + c2.x) / 2, y: (c1.y + c2.y) / 2 }, clientX, clientY);
      }
    },
    [adjustedCorners, screenToCanvas, updateLoupe]
  );

  // ── 7. Global Window Drag Listener (Smooth, Never Drops) ───────────
  useEffect(() => {
    if (!draggingTarget) return;

    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag || !drag.startCorners) return;
      e.preventDefault();

      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      const clientY = e.clientY ?? e.touches?.[0]?.clientY;
      if (clientX == null || clientY == null) return;

      const pt = screenToCanvas(clientX, clientY);
      if (!pt) return;

      if (drag.type === "corner") {
        const x = Math.max(0, Math.min(canvas.width, pt.x));
        const y = Math.max(0, Math.min(canvas.height, pt.y));

        setAdjustedCorners((prev) =>
          prev.map((c, i) => (i === drag.index ? { x, y } : c))
        );
        updateLoupe({ x, y }, clientX, clientY);
      } else if (drag.type === "edge") {
        const i1 = drag.index;
        const i2 = (drag.index + 1) % 4;
        const c1 = drag.startCorners[i1];
        const c2 = drag.startCorners[i2];

        const rawDx = pt.x - drag.startPt.x;
        const rawDy = pt.y - drag.startPt.y;

        const minDx = Math.max(-c1.x, -c2.x);
        const maxDx = Math.min(canvas.width - c1.x, canvas.width - c2.x);
        const dx = Math.max(minDx, Math.min(maxDx, rawDx));

        const minDy = Math.max(-c1.y, -c2.y);
        const maxDy = Math.min(canvas.height - c1.y, canvas.height - c2.y);
        const dy = Math.max(minDy, Math.min(maxDy, rawDy));

        const newC1 = { x: c1.x + dx, y: c1.y + dy };
        const newC2 = { x: c2.x + dx, y: c2.y + dy };

        setAdjustedCorners((prev) =>
          prev.map((c, i) => {
            if (i === i1) return newC1;
            if (i === i2) return newC2;
            return c;
          })
        );

        const midpoint = { x: (newC1.x + newC2.x) / 2, y: (newC1.y + newC2.y) / 2 };
        updateLoupe(midpoint, clientX, clientY);
      }
    };

    const onEnd = () => {
      dragRef.current = null;
      setDraggingTarget(null);
      setLoupeState((prev) => ({ ...prev, visible: false }));
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [draggingTarget, canvas, screenToCanvas, updateLoupe]);

  // ── 8. Quick Actions: Reset / Full Page ────────────────────────────
  const handleReset = () => {
    if (initialCornersRef.current) {
      setAdjustedCorners(initialCornersRef.current.map((c) => ({ ...c })));
    }
  };

  const handleFullPage = () => {
    setAdjustedCorners([
      { x: 0, y: 0 },
      { x: canvas.width, y: 0 },
      { x: canvas.width, y: canvas.height },
      { x: 0, y: canvas.height },
    ]);
  };

  if (!adjustedCorners) return null;

  const vbW = canvas.width;
  const vbH = canvas.height;
  const handleR = Math.max(14, vbW * 0.022);
  const pillW = Math.max(36, vbW * 0.05);
  const pillH = Math.max(12, vbW * 0.018);
  const edgeTouchW = Math.max(70, vbW * 0.095);
  const edgeTouchH = Math.max(50, vbW * 0.075);

  const lerp = (p1, p2, t) => ({
    x: (1 - t) * p1.x + t * p2.x,
    y: (1 - t) * p1.y + t * p2.y,
  });

  const c0 = adjustedCorners[0];
  const c1 = adjustedCorners[1];
  const c2 = adjustedCorners[2];
  const c3 = adjustedCorners[3];

  const v1Top = lerp(c0, c1, 1 / 3);
  const v1Bottom = lerp(c3, c2, 1 / 3);
  const v2Top = lerp(c0, c1, 2 / 3);
  const v2Bottom = lerp(c3, c2, 2 / 3);

  const h1Left = lerp(c0, c3, 1 / 3);
  const h1Right = lerp(c1, c2, 1 / 3);
  const h2Left = lerp(c0, c3, 2 / 3);
  const h2Right = lerp(c1, c2, 2 / 3);

  return (
    <div className="ds-crop-view">
      {/* Top Header */}
      <div className="ds-crop-header">
        <div className="ds-crop-title">Adjust borders to match document</div>
        <div className="ds-crop-tools">
          <button
            type="button"
            className="ds-crop-tool-btn"
            onClick={handleReset}
            title="Reset to detected edges"
          >
            ⟲ Auto-Detect
          </button>
          <button
            type="button"
            className="ds-crop-tool-btn"
            onClick={handleFullPage}
            title="Select full picture"
          >
            ⤢ Full Page
          </button>
        </div>
      </div>

      {/* Main Image Area with Stage & SVG Overlay */}
      <div ref={containerRef} className="ds-crop-image-area">
        {/* Stage strictly sized to image aspect ratio */}
        <div
          ref={stageRef}
          className="ds-crop-stage"
          style={{
            position: "absolute",
            left: stageDims.left || 0,
            top: stageDims.top || 0,
            width: stageDims.w || "100%",
            height: stageDims.h || "100%",
          }}
        >
          {displayUrl && (
            <img
              src={displayUrl}
              alt="Captured"
              className="ds-crop-image"
              draggable={false}
            />
          )}

          {/* SVG Overlay covering 100% of stage */}
          <svg
            className="ds-crop-overlay"
            viewBox={`0 0 ${vbW} ${vbH}`}
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              overflow: "visible",
            }}
          >
            {/* Dim area outside quadrilateral */}
            <defs>
              <mask id="ds-crop-mask">
                <rect width={vbW} height={vbH} fill="white" />
                <polygon
                  points={adjustedCorners.map((c) => `${c.x},${c.y}`).join(" ")}
                  fill="black"
                />
              </mask>
            </defs>
            <rect
              width={vbW}
              height={vbH}
              fill="rgba(0, 0, 0, 0.58)"
              mask="url(#ds-crop-mask)"
            />

            {/* 3x3 Perspective Grid */}
            <g
              className="ds-crop-grid"
              stroke="rgba(33, 150, 243, 0.45)"
              strokeWidth={Math.max(1, vbW * 0.0015)}
              strokeDasharray={`${vbW * 0.008},${vbW * 0.006}`}
            >
              <line x1={v1Top.x} y1={v1Top.y} x2={v1Bottom.x} y2={v1Bottom.y} />
              <line x1={v2Top.x} y1={v2Top.y} x2={v2Bottom.x} y2={v2Bottom.y} />
              <line x1={h1Left.x} y1={h1Left.y} x2={h1Right.x} y2={h1Right.y} />
              <line x1={h2Left.x} y1={h2Left.y} x2={h2Right.x} y2={h2Right.y} />
            </g>

            {/* Quadrilateral Boundary Outline */}
            <polygon
              points={adjustedCorners.map((c) => `${c.x},${c.y}`).join(" ")}
              fill="rgba(33, 150, 243, 0.08)"
              stroke="#2196F3"
              strokeWidth={Math.max(2.8, vbW * 0.0035)}
              strokeLinejoin="round"
            />

            {/* ── 4 Draggable Mid-Edge Handles (Sides) ── */}
            {adjustedCorners.map((c, i) => {
              const next = adjustedCorners[(i + 1) % 4];
              const mx = (c.x + next.x) / 2;
              const my = (c.y + next.y) / 2;
              const angleDeg =
                Math.atan2(next.y - c.y, next.x - c.x) * (180 / Math.PI);
              const isDraggingThis =
                draggingTarget?.type === "edge" && draggingTarget?.index === i;

              return (
                <g
                  key={`edge-handle-${i}`}
                  transform={`translate(${mx}, ${my}) rotate(${angleDeg})`}
                  className={`ds-crop-edge-group ${isDraggingThis ? "ds-dragging" : ""}`}
                  onPointerDown={handlePointerDown("edge", i)}
                  onTouchStart={handlePointerDown("edge", i)}
                  style={{ cursor: "grab" }}
                >
                  {/* Generous touch hit box */}
                  <rect
                    x={-edgeTouchW / 2}
                    y={-edgeTouchH / 2}
                    width={edgeTouchW}
                    height={edgeTouchH}
                    fill="transparent"
                  />
                  {/* Pill capsule handle */}
                  <rect
                    x={-pillW / 2}
                    y={-pillH / 2}
                    width={pillW}
                    height={pillH}
                    rx={pillH / 2}
                    fill={isDraggingThis ? "#1976D2" : "#2196F3"}
                    stroke="white"
                    strokeWidth={Math.max(2, vbW * 0.0025)}
                    className="ds-crop-edge-pill"
                  />
                  {/* Center grip bars */}
                  <line
                    x1={-3}
                    y1={-pillH * 0.3}
                    x2={-3}
                    y2={pillH * 0.3}
                    stroke="white"
                    strokeWidth={Math.max(1.2, vbW * 0.0018)}
                    strokeLinecap="round"
                  />
                  <line
                    x1={3}
                    y1={-pillH * 0.3}
                    x2={3}
                    y2={pillH * 0.3}
                    stroke="white"
                    strokeWidth={Math.max(1.2, vbW * 0.0018)}
                    strokeLinecap="round"
                  />
                </g>
              );
            })}

            {/* ── 4 Draggable Corner Handles ── */}
            {adjustedCorners.map((c, i) => {
              const isDraggingThis =
                draggingTarget?.type === "corner" && draggingTarget?.index === i;

              return (
                <g
                  key={`corner-handle-${i}`}
                  transform={`translate(${c.x}, ${c.y})`}
                  className={`ds-crop-corner-group ${isDraggingThis ? "ds-dragging" : ""}`}
                  onPointerDown={handlePointerDown("corner", i)}
                  onTouchStart={handlePointerDown("corner", i)}
                  style={{ cursor: "grab" }}
                >
                  {/* Generous touch target */}
                  <circle
                    r={handleR * 2.2}
                    fill="transparent"
                  />
                  {/* Outer ring / handle body */}
                  <circle
                    r={handleR}
                    fill={isDraggingThis ? "#1976D2" : "#2196F3"}
                    stroke="white"
                    strokeWidth={Math.max(2.8, vbW * 0.0035)}
                    className="ds-crop-corner-handle"
                  />
                  {/* Inner center dot */}
                  <circle
                    r={handleR * 0.32}
                    fill="white"
                  />
                </g>
              );
            })}
          </svg>
        </div>

        {/* Magnifying Loupe */}
        <div
          className="ds-crop-loupe"
          style={{
            display: loupeState.visible ? "block" : "none",
            left: loupeState.x,
            top: loupeState.y,
          }}
        >
          <canvas
            ref={loupeCanvasRef}
            width={110}
            height={110}
            className="ds-crop-loupe-canvas"
          />
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="ds-crop-actions">
        <button
          type="button"
          className="ds-btn ds-btn-secondary"
          onClick={onCancel}
        >
          ✕ Cancel
        </button>
        <button
          type="button"
          className="ds-btn ds-btn-primary"
          onClick={() => onApply(adjustedCorners)}
        >
          ✓ Done
        </button>
      </div>
    </div>
  );
};

export default CropAdjustView;
